<template>
  <!-- Master-only edit/add panel for a single Account-Setup catalog object
       (Machine / Assembly / Sub-Assembly / Part / Wear). Pre-fills via
       getSetupEntity; saves via setupEdit (existing) or setupCreate (new), both
       of which log a restorable history entry. Wear is EDIT-only here (new wear
       is added via the wear add-search). -->
  <div class="setup-obj-form">
    <h4 class="sof-title">{{ title }}</h4>

    <div v-if="loadErr" class="sof-msg sof-err">
      Couldn't load this {{ kindNoun }}.
      <button type="button" class="sof-link" @click="load">Retry</button>
    </div>

    <div v-else>
      <!-- MACHINE -->
      <template v-if="kind === 'machine'">
        <label class="sof-field"><span class="sof-lbl">Code</span>
          <input v-model="m.code" type="text" maxlength="60" :disabled="busy" /></label>
        <label class="sof-field"><span class="sof-lbl">Description</span>
          <input v-model="m.description" type="text" maxlength="200" :disabled="busy" /></label>
      </template>

      <!-- ASSEMBLY / SUB-ASSEMBLY -->
      <template v-else-if="kind === 'assembly'">
        <label class="sof-field"><span class="sof-lbl">Name</span>
          <input v-model="m.name" type="text" maxlength="200" :disabled="busy" /></label>
        <label class="sof-field"><span class="sof-lbl">Machine</span>
          <select v-model="m.machineId" :disabled="busy">
            <option value="0">(none)</option>
            <option v-for="o in machineOpts" :key="'mc-' + o.value" :value="o.value">{{ o.text }}</option>
          </select></label>
        <label class="sof-field"><span class="sof-lbl">Parent assembly</span>
          <select v-model="m.parentAssemblyId" :disabled="busy">
            <option value="0">(none — top-level assembly)</option>
            <option v-for="o in assemblyOpts" :key="'pa-' + o.value" :value="o.value">{{ o.text }}</option>
          </select>
          <small class="sof-hint">Pick a parent to make this a sub-assembly.</small></label>
      </template>

      <!-- PART -->
      <template v-else-if="kind === 'part'">
        <label class="sof-field"><span class="sof-lbl">Code</span>
          <input v-model="m.code" type="text" maxlength="60" :disabled="busy" /></label>
        <label class="sof-field"><span class="sof-lbl">Description</span>
          <input v-model="m.description" type="text" maxlength="300" :disabled="busy" /></label>
        <label class="sof-field"><span class="sof-lbl">Item #</span>
          <input v-model="m.item" type="text" maxlength="60" :disabled="busy" placeholder="e.g. 70AS" /></label>
        <div class="sof-row">
          <label class="sof-field sof-half"><span class="sof-lbl">Qty / machine</span>
            <input v-model="m.quantityPerMachine" type="number" min="0" :disabled="busy" /></label>
          <label class="sof-field sof-half"><span class="sof-lbl">Price</span>
            <input v-model="m.price" type="number" min="0" step="0.01" :disabled="busy" /></label>
        </div>
        <div class="sof-row">
          <label class="sof-field sof-half"><span class="sof-lbl">Sequence</span>
            <input v-model="m.sequence" type="number" step="0.1" :disabled="busy" /></label>
        </div>
        <label class="sof-field"><span class="sof-lbl">Assembly</span>
          <select v-model="m.assemblyId" :disabled="busy">
            <option value="0">(pick an assembly)</option>
            <option v-for="o in assemblyOpts" :key="'pa2-' + o.value" :value="o.value">{{ o.text }}</option>
          </select></label>
      </template>

      <!-- WEAR (edit only) -->
      <template v-else-if="kind === 'wear'">
        <label class="sof-field"><span class="sof-lbl">Assembly</span>
          <select v-model="m.assemblyId" :disabled="busy" @change="loadPartsForAssembly">
            <option value="0">(none)</option>
            <option v-for="o in assemblyOpts" :key="'wa-' + o.value" :value="o.value">{{ o.text }}</option>
          </select></label>
        <label class="sof-field"><span class="sof-lbl">Part</span>
          <select v-model="m.partId" :disabled="busy">
            <option value="0">(pick a part)</option>
            <option v-for="o in partOpts" :key="'wp-' + o.value" :value="o.value">{{ o.text }}</option>
          </select>
          <small v-if="!partOpts.length" class="sof-hint">Choose an assembly to list its parts.</small></label>
        <div class="sof-row">
          <label class="sof-field sof-half"><span class="sof-lbl">Qty / machine</span>
            <input v-model="m.quantityPerMachine" type="number" min="0" :disabled="busy" /></label>
          <label class="sof-field sof-half sof-check"><input type="checkbox" v-model="m.pending" :disabled="busy" /> <span>Pending</span></label>
        </div>
      </template>

      <!-- Active (all kinds) -->
      <label class="sof-field sof-check">
        <input type="checkbox" v-model="m.active" :disabled="busy" />
        <span>Active <small class="sof-hint">(unchecking hides it; same as a soft-delete)</small></span>
      </label>

      <div v-if="errorMsg" class="sof-msg sof-err">{{ errorMsg }}</div>

      <div class="sof-actions">
        <button type="button" class="sof-save" :disabled="busy" @click="save">
          {{ busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Create') }}
        </button>
        <button type="button" class="sof-cancel" :disabled="busy" @click="$emit('cancel')">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";
const feApi = new FeApiService();

export default {
  name: "SetupObjectForm",
  props: {
    // machine | assembly | part | wear
    kind: { type: String, required: true },
    // Edit: the entity id (Part/Machine/Assembly id; for WEAR the Wear.Id). Null/0 = add new.
    entityId: { type: [Number, String], default: null },
    companyId: { type: [Number, String], default: 0 },
    // UI history bucket: machines | assemblies | subAssemblies | parts | wearParts
    category: { type: String, default: "" },
  },
  data() {
    return {
      busy: false,
      loadErr: false,
      errorMsg: "",
      m: {
        code: "", description: "", item: "", name: "", active: true,
        quantityPerMachine: "1", price: "0", sequence: "0", pending: false,
        machineId: "0", parentAssemblyId: "0", assemblyId: "0", partId: "0",
      },
      machineOpts: [],
      assemblyOpts: [],
      partOpts: [],
    };
  },
  computed: {
    isEdit() {
      return this.entityId !== null && this.entityId !== "" && Number(this.entityId) > 0;
    },
    kindNoun() {
      return { machine: "machine", assembly: "assembly", part: "part", wear: "wear part" }[this.kind] || "object";
    },
    title() {
      const noun = { machine: "Machine", assembly: "Assembly", part: "Part", wear: "Wear Part" }[this.kind] || "Object";
      const sub = this.category === "subAssemblies" && this.kind === "assembly" ? "Sub-Assembly" : noun;
      return (this.isEdit ? "Edit " : "Add new ") + sub;
    },
  },
  mounted() {
    this.loadOptions();
    if (this.isEdit) this.load();
  },
  methods: {
    loadOptions() {
      const self = this;
      if (this.kind === "assembly") {
        feApi.getMachinesList().then((r) => { self.machineOpts = r || []; }).catch(() => {});
        feApi.getAssembliesList().then((r) => { self.assemblyOpts = r || []; }).catch(() => {});
      } else if (this.kind === "part" || this.kind === "wear") {
        feApi.getAssembliesList().then((r) => { self.assemblyOpts = r || []; }).catch(() => {});
      }
    },
    loadPartsForAssembly() {
      const self = this;
      const aid = Number(this.m.assemblyId) || 0;
      if (!aid) { this.partOpts = []; return; }
      feApi.getSelectItemsByAssembly({ id: aid })
        .then((r) => { self.partOpts = r || []; })
        .catch(() => { self.partOpts = []; });
    },
    load() {
      const self = this;
      this.loadErr = false;
      feApi.getSetupEntity(this.kind, this.entityId)
        .then((d) => {
          const f = (d && d.fields) || {};
          const bool = (v) => v === true || v === "true" || v === "True";
          self.m.code = f.code || "";
          self.m.description = f.description || "";
          self.m.item = f.item || "";
          self.m.name = f.name || "";
          self.m.quantityPerMachine = f.quantityPerMachine != null ? String(f.quantityPerMachine) : "1";
          self.m.price = f.price != null ? String(f.price) : "0";
          self.m.sequence = f.sequence != null ? String(f.sequence) : "0";
          self.m.machineId = f.machineId != null ? String(f.machineId) : "0";
          self.m.parentAssemblyId = f.parentAssemblyId != null ? String(f.parentAssemblyId) : "0";
          self.m.assemblyId = f.assemblyId != null ? String(f.assemblyId) : "0";
          self.m.partId = f.partId != null ? String(f.partId) : "0";
          self.m.pending = bool(f.pending);
          self.m.active = f.active != null ? bool(f.active) : true;
          if (self.kind === "wear") self.loadPartsForAssembly();
        })
        .catch(() => { self.loadErr = true; });
    },
    save() {
      if (this.busy) return;
      const self = this;
      const k = this.kind;
      const cid = Number(this.companyId) || Number(localStorage.companyId) || 0;
      const ab = this.m.active ? "true" : "false";
      let fields;
      if (k === "machine") {
        fields = { code: this.m.code, description: this.m.description, active: ab };
      } else if (k === "assembly") {
        if (!this.m.name) { this.errorMsg = "A name is required."; return; }
        fields = {
          name: this.m.name,
          machineId: String(Number(this.m.machineId) || 0),
          parentAssemblyId: String(Number(this.m.parentAssemblyId) || 0),
          active: ab,
        };
      } else if (k === "part") {
        if (!(Number(this.m.assemblyId) > 0)) { this.errorMsg = "Pick an assembly for this part."; return; }
        fields = {
          code: this.m.code, description: this.m.description, item: this.m.item,
          quantityPerMachine: String(Number(this.m.quantityPerMachine) || 0),
          price: String(Number(this.m.price) || 0),
          sequence: String(Number(this.m.sequence) || 0),
          assemblyId: String(Number(this.m.assemblyId) || 0),
          active: ab,
        };
      } else if (k === "wear") {
        if (!(Number(this.m.partId) > 0)) { this.errorMsg = "Pick a part for this wear item."; return; }
        fields = {
          assemblyId: String(Number(this.m.assemblyId) || 0),
          partId: String(Number(this.m.partId) || 0),
          quantityPerMachine: String(Number(this.m.quantityPerMachine) || 1),
          pending: this.m.pending ? "true" : "false",
          active: ab,
        };
      } else {
        this.errorMsg = "Unsupported object type.";
        return;
      }

      this.busy = true;
      this.errorMsg = "";
      const req = this.isEdit
        ? feApi.setupEdit({ type: k, id: Number(this.entityId), companyId: cid, category: this.category, fields })
        : feApi.setupCreate({ type: k, companyId: cid, category: this.category, fields });
      req
        .then((res) => {
          self.busy = false;
          self.$emit("saved", { kind: k, category: self.category, id: (res && res.id) || Number(self.entityId) || 0 });
        })
        .catch((err) => {
          self.busy = false;
          const s = err && err.response && err.response.status;
          self.errorMsg = (s === 401 || s === 403)
            ? "You don't have permission to save (Master only)."
            : "Couldn't save — please check the fields and try again.";
        });
    },
  },
};
</script>

<style scoped>
.setup-obj-form { padding: 4px 2px 8px; }
.sof-title { font-size: 16px; font-weight: 800; color: #16181b; margin: 0 0 12px; }
.sof-field { display: block; margin: 0 0 11px; }
.sof-lbl { display: block; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: #6b7280; margin-bottom: 3px; }
.sof-field input[type="text"],
.sof-field input[type="number"],
.sof-field select {
  width: 100%; padding: 7px 9px; font-size: 14px; color: #16181b;
  background: #fff; border: 1px solid #d4d8dd; border-radius: 6px; box-sizing: border-box;
}
.sof-field input:focus, .sof-field select:focus { outline: none; border-color: #c0392b; box-shadow: 0 0 0 2px rgba(192,57,43,.15); }
.sof-row { display: flex; gap: 10px; }
.sof-half { flex: 1 1 0; }
.sof-check { display: flex; align-items: center; gap: 8px; }
.sof-check input { width: auto; }
.sof-check span { font-size: 13px; color: #16181b; font-weight: 600; }
.sof-hint { display: inline; font-weight: 400; text-transform: none; letter-spacing: 0; color: #9098a0; font-size: 11px; }
.sof-msg { font-size: 13px; margin: 6px 0 10px; }
.sof-err { color: #c0392b; font-weight: 600; }
.sof-link { background: none; border: none; color: #c0392b; text-decoration: underline; cursor: pointer; padding: 0; font: inherit; }
.sof-actions { display: flex; gap: 10px; margin-top: 14px; }
.sof-save { padding: 8px 18px; font-size: 14px; font-weight: 700; color: #fff; background: #2e7d32; border: none; border-radius: 6px; cursor: pointer; }
.sof-save:disabled { background: #9aa7b0; cursor: default; }
.sof-cancel { padding: 8px 16px; font-size: 14px; font-weight: 600; color: #444; background: transparent; border: 1px solid #c4c9cf; border-radius: 6px; cursor: pointer; }
</style>
